import Map from 'ol/Map'
import { Feature, View } from 'ol'
import { fromLonLat, get as getProjection, transform } from 'ol/proj'
import { getWidth } from 'ol/extent'
import TileLayer from 'ol/layer/Tile'
import WMTS from 'ol/source/WMTS'
import WMTSTileGrid from 'ol/tilegrid/WMTS'
import { defaults } from 'ol/interaction/defaults'
import { Fill, Stroke, Style } from 'ol/style'
import CircleStyle from 'ol/style/Circle'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { StyleFunction } from 'ol/style/Style'
import { StyleUtils } from './style.utils'
import { LineString, MultiLineString, Point } from 'ol/geom'
import { FeatureLike } from 'ol/Feature'

export class MapUtils {

    private static enedisGeoJson: any = null;

    static initMap(isCadastreEnabled: boolean): Map {
        const map = new Map({
            target: 'geoMap',
            view: new View({
                zoom: 19,
                center: fromLonLat([2.6371920837237948, 48.833179106831004])            
            })
        })

        const resolutions = []
        const matrixIds = []
        const proj3857 = getProjection('EPSG:3857')
        const maxResolution = getWidth(proj3857.getExtent()) / 256
        for (let i = 0; i < 20; i++) {
            matrixIds[i] = i.toString()
            resolutions[i] = maxResolution / Math.pow(2, i)
        }

        // Couche de fond satellique
        const baseLayer  = new TileLayer({
            source: new WMTS({
                url: 'https://data.geopf.fr/wmts',
                layer: 'ORTHOIMAGERY.ORTHOPHOTOS',
                matrixSet: 'PM',
                format: 'image/jpeg',
                projection: 'EPSG:3857',
                tileGrid: new WMTSTileGrid({
                    origin: [-20037508, 20037508],
                    resolutions: resolutions,
                    matrixIds: matrixIds
                }),
                style: 'normal',
                crossOrigin: 'anonymous'
            })
        })

        // Couche cadastrale
        const cadastreLayer = new TileLayer({
            source: new WMTS({
                url: 'https://data.geopf.fr/wmts',
                layer: 'CADASTRALPARCELS.PARCELLAIRE_EXPRESS',
                matrixSet: 'PM',
                format: 'image/png',
                projection: 'EPSG:3857',
                tileGrid: new WMTSTileGrid({
                    origin: [-20037508, 20037508],
                    resolutions: resolutions,
                    matrixIds: matrixIds
                }),
                style: 'normal',
                crossOrigin: 'anonymous'
            })
        });

        cadastreLayer.getSource().on('tileloaderror', function (event) {
            console.error('Erreur de chargement des tuiles (cadastre) : ', event);
        });
        baseLayer.getSource().on('tileloaderror', function (event) {
            console.error('Erreur de chargement des tuiles (fond) : ', event);
        });

        map.addLayer(baseLayer);
        if (isCadastreEnabled) {
            map.addLayer(cadastreLayer);
        }

        // --- Couche Enedis BT ---
        const enedisLayer = MapUtils.createEnedisBTLayer();
        enedisLayer.setVisible(false); // ← DÉSACTIVÉ
        map.addLayer(enedisLayer);
        (map as any).enedisLayer = enedisLayer;

        // --- Couche HTA ---
        const htaLayer = MapUtils.createHtaLayer();
        htaLayer.setVisible(false); // ← DÉSACTIVÉ
        map.addLayer(htaLayer);
        (map as any).htaLayer = htaLayer;

        // --- POSTES ---
        const postesLayer = MapUtils.createPostesLayer(map);
        postesLayer.setVisible(false);
        map.addLayer(postesLayer);
        (map as any).postesLayer = postesLayer;
        // --- POSTES Hta hta ---
        const postesHtaHtaLayer = MapUtils.createPostesHtaHtaLayer(map);
        postesHtaHtaLayer.setVisible(false);
        map.addLayer(postesHtaHtaLayer);
        (map as any).postesHtaHtaLayer = postesHtaHtaLayer;


        // Écouteur moveend
        let pending = false;
        map.on('moveend', () => {
            if (pending) return;
            pending = true;
            setTimeout(() => {
                const enedisLayer = (map as any).enedisLayer;
                const htaLayer = (map as any).htaLayer;
                const postes = (map as any).postesLayer;
                const postesHta = (map as any).postesHtaLayer;


                if (enedisLayer?.getVisible()) MapUtils.updateEnedisBTVisible(map, enedisLayer);
                if (htaLayer?.getVisible()) MapUtils.updateHtaVisible(map, htaLayer);
                if (postes?.getVisible()) MapUtils.updatePostesVisible(map, postes);
                if (postesHta?.getVisible()) MapUtils.updatePostesHtaHtaVisible(map, postesHta);
                pending = false;
            }, 300);
        });
        (map as any).cadastreLayer = cadastreLayer;
        return map;
    }

    static updateMapLayers(map: Map, isCadastreEnabled: boolean): void {
        const cadastreLayer = (map as any).cadastreLayer as TileLayer<any>;
        const layers = map.getLayers().getArray();

        if (isCadastreEnabled && !layers.includes(cadastreLayer)) {
        map.addLayer(cadastreLayer);
        } else if (!isCadastreEnabled && layers.includes(cadastreLayer)) {
        map.removeLayer(cadastreLayer);
        }
    }


   static resetMapToDefaultInteractions(map: Map) {
        map.getInteractions().clear()
        defaults().forEach(interaction => map.addInteraction(interaction))
    }

    /** Convertit une couleur hex (#RRGGBB ou #RRGGBBAA) en rgba(...) avec alpha donné. */
    private static withAlpha(hexOrCss: string, alpha: number): string {
        const trimmed = (hexOrCss || '').trim()
        const match = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(trimmed)
        if (!match) return trimmed
        const hex = match[1]
        const r = parseInt(hex.slice(0, 2), 16)
        const g = parseInt(hex.slice(2, 4), 16)
        const b = parseInt(hex.slice(4, 6), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    static buildExclusionLayer(_color: string) {
        return new VectorLayer<any>({
            source: new VectorSource(),
            style: new Style({
                fill: new Fill({ color: 'rgba(220, 30, 30, 0.30)' }),
                stroke: new Stroke({ color: '#dc1e1e', width: 2, lineDash: [6, 4] })
            }),
            zIndex: 5
        })
    }

    static buildPanneauLayer(_color: string) {
        return new VectorLayer<any>({
            source: new VectorSource(),
            style: new Style({
                fill: new Fill({ color: 'rgba(0, 0, 0, 0.2)' }),
                stroke: new Stroke({ color: '#000000', width: 1 })
            }),
            zIndex: 10
        })
    }

    static buildVectorLayer(color: string, colorSurface: string, _hasCalepinage?: () => boolean) {
        const plainSurfaceStyle = new Style({
            fill: new Fill({ color: MapUtils.withAlpha(colorSurface, 0.25) }),
            stroke: new Stroke({ color: color, width: 3 })
        })

        const layerStyleFunction = (feature: Feature) => {
            const geomType = feature.getGeometry()?.getType()
            const isPolygon = geomType === 'Polygon' || geomType === 'MultiPolygon'

            const baseStyle = isPolygon
                ? plainSurfaceStyle
                : new Style({
                    fill: new Fill({ color: colorSurface }),
                    stroke: new Stroke({ color: color, width: 5 }),
                    image: new CircleStyle({
                        fill: new Fill({ color: colorSurface }),
                        radius: 7
                    })
                })

            const styles = [baseStyle, ...StyleUtils.buildAzimuthArrowStyle(feature)]

            return styles
        }

        return new VectorLayer<any>({
            source: new VectorSource(),
            style: (layerStyleFunction as StyleFunction)
        })
    }

        // --- Création de la couche Enedis ---
    static createEnedisBTLayer(): VectorLayer<VectorSource> {
        const source = new VectorSource();
        const layer = new VectorLayer({
            source,
            style: StyleUtils.buildFixedScreenLineStyleFunction('#005EB8', 3),
            updateWhileAnimating: true,
            updateWhileInteracting: true,
            zIndex: 100
        });
        (layer as any).set('name', 'Enedis BT Souterraines');
        return layer;
    }

    static createHtaLayer(): VectorLayer<VectorSource> {
        const source = new VectorSource();
        const layer = new VectorLayer({
            source,
            style: StyleUtils.buildFixedScreenLineStyleFunction('#994878', 4),
            updateWhileAnimating: true,
            updateWhileInteracting: true,
            zIndex: 101
        });
        (layer as any).set('name', 'Enedis HTA Souterraines');
        return layer;
    }

static createPostesLayer(map: Map): VectorLayer<VectorSource> {
    const source = new VectorSource();
    const layer = new VectorLayer({
        source,
        style: StyleUtils.buildFixedPointStyleFunction('#00A3E0', 8, '#000000', 2, map),
        updateWhileAnimating: true,
        updateWhileInteracting: true,
        zIndex: 102
    });
    (layer as any).set('name', 'Postes HTA/BT');
    return layer;
}

static createPostesHtaHtaLayer(map: Map): VectorLayer<VectorSource> {
    const source = new VectorSource();
    const layer = new VectorLayer({
        source,
        style: StyleUtils.buildFixedPointStyleFunction('#ffc107', 8, '#000000', 2, map),
        updateWhileAnimating: true,
        updateWhileInteracting: true,
        zIndex: 102
    });
    (layer as any).set('name', 'Postes HTA/HTA');
    return layer;
}

    private static async loadEnedisBTForBBox(minLon: number, minLat: number, maxLon: number, maxLat: number): Promise<any> {
        const wkt = `POLYGON((${minLon} ${minLat}, ${maxLon} ${minLat}, ${maxLon} ${maxLat}, ${minLon} ${maxLat}, ${minLon} ${minLat}))`;
        const whereClause = `intersects(geo_shape, geom'${wkt}')`;
        const url = `https://opendata.enedis.fr/api/explore/v2.1/catalog/datasets/reseau-souterrain-bt/exports/geojson?limit=-1&offset=0&where=${encodeURIComponent(whereClause)}`;
        
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            const features = data.features || [];
            if (features.length === 0) {
                return { type: 'FeatureCollection', features: [] };
            }
            return { type: 'FeatureCollection', features };
        } catch (err) {
            return { type: 'FeatureCollection', features: [] };
        }
    }

    private static async loadHtaForBBox(minLon: number, minLat: number, maxLon: number, maxLat: number): Promise<any> {
        const wkt = `POLYGON((${minLon} ${minLat}, ${maxLon} ${minLat}, ${maxLon} ${maxLat}, ${minLon} ${maxLat}, ${minLon} ${minLat}))`;
        const whereClause = `intersects(geo_shape, geom'${wkt}')`;
        const url = `https://opendata.enedis.fr/api/explore/v2.1/catalog/datasets/reseau-souterrain-hta/exports/geojson?limit=-1&offset=0&where=${encodeURIComponent(whereClause)}`;

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            const features = data.features || [];
            return { type: 'FeatureCollection', features };
        } catch (err) {
            return { type: 'FeatureCollection', features: [] };
        }
    }

    private static async loadPostesForBBox(minLon: number, minLat: number, maxLon: number, maxLat: number): Promise<any> {
        // BBox en WGS84 (lon/lat)
        const wkt = `POLYGON((${minLon} ${minLat}, ${maxLon} ${minLat}, ${maxLon} ${maxLat}, ${minLon} ${maxLat}, ${minLon} ${minLat}))`;
        
        // Clause where avec intersects (encodée correctement)
        const whereClause = `intersects(geo_point_2d,geom'${wkt}')`;
        const encodedWhere = encodeURIComponent(whereClause); // ← FIX : Encodage complet
        const url = `https://opendata.enedis.fr/api/explore/v2.1/catalog/datasets/poste-electrique/exports/geojson?limit=-1&offset=0&where=${encodedWhere}`;

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            const features = data.features || [];
            return { type: 'FeatureCollection', features };
        } catch (err) {
            return { type: 'FeatureCollection', features: [] };
        }
    }

    private static async loadPostesHtaHtaForBBox(minLon: number, minLat: number, maxLon: number, maxLat: number): Promise<any> {
        // BBox en WGS84 (lon/lat)
        const wkt = `POLYGON((${minLon} ${minLat}, ${maxLon} ${minLat}, ${maxLon} ${maxLat}, ${minLon} ${maxLat}, ${minLon} ${minLat}))`;
        
        // Clause where avec intersects (encodée correctement)
        const whereClause = `intersects(geo_point_2d,geom'${wkt}')`;
        const encodedWhere = encodeURIComponent(whereClause); 
        const url = `https://opendata.enedis.fr/api/explore/v2.1/catalog/datasets/position-geographique-des-poteaux-hta-et-bt/exports/geojson?limit=-1&offset=0&where=${encodedWhere}`;

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            const features = data.features || [];
            return { type: 'FeatureCollection', features };
        } catch (err) {
            return { type: 'FeatureCollection', features: [] };
        }
    }

    static async updateEnedisBTVisible(map: Map, vectorLayer: VectorLayer<VectorSource>) {
        const source = vectorLayer.getSource()!;
        if (!vectorLayer.getVisible()) {
            source.clear();
            return;
        }

        // ----- BBox de la vue en EPSG:4326 (lon/lat) -----
        const extent3857 = map.getView().calculateExtent(map.getSize());
        const [minX, minY, maxX, maxY] = extent3857;
        const bl = transform([minX, minY], 'EPSG:3857', 'EPSG:4326'); // [minLon, minLat]
        const tr = transform([maxX, maxY], 'EPSG:3857', 'EPSG:4326'); // [maxLon, maxLat]

        const data = await this.loadEnedisBTForBBox(bl[0], bl[1], tr[0], tr[1]);
        source.clear();

        
        if (!data?.features?.length) return;

        const visibleFeatures = data.features.map((f: any) => {
            let olGeom;
            if (f.geometry.type === 'LineString') {
                olGeom = new LineString(f.geometry.coordinates);
            } else {
                olGeom = new MultiLineString(f.geometry.coordinates);
    }
            olGeom.transform('EPSG:4326', 'EPSG:3857');

            // On crée la feature
            const feature = new Feature(olGeom); // On passe la géométrie directement ici
            
            // On ajoute les propriétés en excluant la clé 'geometry' qui est un string
            const props = { ...f.properties };
            delete props.geometry; // <--- TRÈS IMPORTANT
            feature.setProperties(props);

            return feature;
        });
                source.addFeatures(visibleFeatures);

    }

    static async updateHtaVisible(map: Map, vectorLayer: VectorLayer<VectorSource>) {
        const source = vectorLayer.getSource()!;
        if (!vectorLayer.getVisible()) {
            source.clear();
            return;
        }

        const extent3857 = map.getView().calculateExtent(map.getSize());
        const [minX, minY, maxX, maxY] = extent3857;
        const bl = transform([minX, minY], 'EPSG:3857', 'EPSG:4326');
        const tr = transform([maxX, maxY], 'EPSG:3857', 'EPSG:4326');

        const data = await this.loadHtaForBBox(bl[0], bl[1], tr[0], tr[1]);
        source.clear();

        if (!data?.features?.length) return;

    const visibleFeatures = data.features.map((f: any) => {
        let olGeom;
        if (f.geometry.type === 'LineString') {
            olGeom = new LineString(f.geometry.coordinates);
        } else {
            olGeom = new MultiLineString(f.geometry.coordinates);
        }
        olGeom.transform('EPSG:4326', 'EPSG:3857');

        // On crée la feature
        const feature = new Feature(olGeom); // On passe la géométrie directement ici
        
        // On ajoute les propriétés en excluant la clé 'geometry' qui est un string
        const props = { ...f.properties };
        delete props.geometry; // <--- TRÈS IMPORTANT
        feature.setProperties(props);

        return feature;
    });

        source.addFeatures(visibleFeatures);
    }

static async updatePostesVisible(map: Map, vectorLayer: VectorLayer<VectorSource>) {
    if (!vectorLayer.getVisible()) {
        vectorLayer.getSource()?.clear();
        return;
    }

    const source = vectorLayer.getSource()!;
    const extent3857 = map.getView().calculateExtent(map.getSize());
    const [minX, minY, maxX, maxY] = extent3857;
    const bl = transform([minX, minY], 'EPSG:3857', 'EPSG:4326');
    const tr = transform([maxX, maxY], 'EPSG:3857', 'EPSG:4326');

    const data = await this.loadPostesForBBox(bl[0], bl[1], tr[0], tr[1]);
    if (!data?.features?.length) return;

    source.clear();

const visibleFeatures = data.features
    .map((f: any) => {
        if (!f.geometry || f.geometry.type !== 'Point') return null;
        const [lon, lat] = f.geometry.coordinates;
        const point = new Point(transform([lon, lat], 'EPSG:4326', 'EPSG:3857'));

        const feature = new Feature(point);
        const props = { ...f.properties };
        delete props.geometry; // <--- Supprime la géométrie texte de l'API
        feature.setProperties(props);
        
        return feature;
    })
    .filter((f: Feature | null): f is Feature => f !== null);

    source.addFeatures(visibleFeatures);
}

    
    static async updatePostesHtaHtaVisible(map: Map, vectorLayer: VectorLayer<VectorSource>) {
    if (!vectorLayer.getVisible()) {
        vectorLayer.getSource()?.clear();
        return;
    }

    const source = vectorLayer.getSource()!;
    const extent3857 = map.getView().calculateExtent(map.getSize());
    const [minX, minY, maxX, maxY] = extent3857;
    const bl = transform([minX, minY], 'EPSG:3857', 'EPSG:4326');
    const tr = transform([maxX, maxY], 'EPSG:3857', 'EPSG:4326');

    const data = await this.loadPostesHtaHtaForBBox(bl[0], bl[1], tr[0], tr[1]);
    if (!data?.features?.length) return;

    source.clear();

const visibleFeatures = data.features
    .map((f: any) => {
        if (!f.geometry || f.geometry.type !== 'Point') return null;
        const [lon, lat] = f.geometry.coordinates;
        const point = new Point(transform([lon, lat], 'EPSG:4326', 'EPSG:3857'));

        const feature = new Feature(point);
        const props = { ...f.properties };
        delete props.geometry; // <--- Supprime la géométrie texte de l'API
        feature.setProperties(props);
        
        return feature;
    })
    .filter((f: Feature | null): f is Feature => f !== null);

    source.addFeatures(visibleFeatures);
}

}
